import { PensionPhaseStatus, ProductType } from "@repo/api";
import { cs, useIsDesktop } from '@repo/ui';
import { navigate, type PageProps } from "gatsby";
import { useMemo, type FC } from 'react';
import { BackNavigationBar, LoggedInBar } from "../../components/AppBar/Bars";
import { BottomNavigationDesktopComponent } from '../../components/BottomNavigation';
import Client from "../../components/DPS/Client";
import ContractDetail from "../../components/DPS/ContractDetail";
import Docs from "../../components/DPS/Docs";
import Transactions from "../../components/DPS/Transactions";
import Export from "../../components/PP/Export";
import { Seo } from "../../components/SEO";
import { Tabs } from "../../components/Tabs";
import {
	ContractIDProvider,
	extractContractIDFromPageProps,
	useContractId,
} from "../../context/ContractIDContext";
import { ContractTypeProvider } from "../../context/ContractTypeContext";
import withProtection from "../../highOrderComponents/withProtection";
import { useSPErrorTabsRedirect } from "../../hooks/useSPError";
import {
	useContractById,
} from "../../queryHooks";
import { useAllowedChanges } from "@repo/logic";

const title = "Penzijní připojištění";

export const AppBar: FC<PageProps> = (pageProps) =>
	useIsDesktop() ? (
		<LoggedInBar BottomNavigationComponent={BottomNavigationDesktopComponent} />
	) : (
		<BackNavigationBar
			backHandler={() => { navigate("/smlouvy/") }}
			title={title}
			id={extractContractIDFromPageProps(pageProps)}
		/>
	);

const PPContent = () => {
	useSPErrorTabsRedirect();
	const { contractId: id } = useContractId();
	const { data, isSuccess } = useContractById({
		id,
		options: { refetchOnMount: false },
	});

	const {
		data: allowedData,
		isSuccess: allowedIsSuccess,
		isError,
	} = useAllowedChanges({
		id,
		options: {
			enabled: Boolean(id),
		},
	});

	const tabs = useMemo(() => {
		return [
			data && data.pensionPhaseStatus === PensionPhaseStatus.Payout
				? null
				: { label: cs.contrct.tabs.detail, slug: "detail", disabled: isError },
			{ label: cs.contrct.tabs.payments, slug: "pohyby-platby", disabled: isError },
			{ label: cs.contrct.tabs.client, slug: "klient", disabled: isError },
			{ label: cs.contrct.tabs.contract, slug: "smlouva", disabled: isError },
			{ label: cs.contrct.tabs.docs, slug: "dokumenty" },
		]
	}, [data?.pensionPhaseStatus, isError])

	if (isSuccess) {
		return (
			<Tabs
				appBar={<BackNavigationBar backHandler={() => { navigate("/smlouvy/") }} title={title} />}
				queryParamKey="main-tab"
				tabs={tabs}
			>
				{data.pensionPhaseStatus === PensionPhaseStatus.Payout ? null : (
					<Export />
				)}
				<Transactions />
				<Client />
				<ContractDetail
					pendingChanges={allowedData?.pendingChanges || undefined}
					isSuccessPendingChanges={allowedIsSuccess}
				/>
				<Docs />
			</Tabs>
		);
	}

	return <>{cs.global.pageIsLoading}</>;
};

const PenzijniPripojisteni: FC<PageProps> = (pageProps) => {
	return (
		<ContractIDProvider contractId={extractContractIDFromPageProps(pageProps)}>
			<ContractTypeProvider contractType={ProductType.Pf}>
				<PPContent />
			</ContractTypeProvider>
		</ContractIDProvider>
	);
}

export default withProtection(PenzijniPripojisteni);

export const Head: FC = () => (
	<Seo title={title}>
		<meta name="robots" content="noindex" />
	</Seo>
);
